import React, { FC, memo, useMemo, ReactNode } from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import gfm from 'remark-gfm';
import breaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import Abnormal from '@home/components/Abnormal';
import BasicImage from '@home/components/BasicImage';
import { SKILL_DETAIL_SECTION_ICONS } from '../../../constants/skillDetail';
import { formatFileSize } from '../../../utils/skillMd';
import './index.scss';

/**
 * SKILL.md Markdown 预览组件 Props
 */
export interface ISkillMarkdownViewerProps {
  /** Markdown 正文内容 */
  content?: string;
  /** 文件大小（字节） */
  size?: number;
  /** 是否加载中 */
  loading?: boolean;
  /** 错误信息 */
  error?: Error | null;
  /** ReactMarkdown components 覆盖（用于注入锚点 id 等） */
  components?: Components;
  /** 右侧插槽（TOC 目录），与 markdown 内容左右布局 */
  aside?: ReactNode;
}

const PREFIX = 'dba-skill-md-viewer';

/**
 * SKILL.md Markdown 预览（右侧可插槽 TOC）
 */
const SkillMarkdownViewer: FC<ISkillMarkdownViewerProps> = ({
  content,
  size,
  loading = false,
  error = null,
  components,
  aside,
}) => {
  const sizeText = useMemo(
    () => (typeof size === 'number' ? formatFileSize(size) : ''),
    [size],
  );

  const isStatusView = loading || Boolean(error) || !content;
  const hasAside = Boolean(content && aside);

  return (
    <div className={PREFIX}>
      <div className={`${PREFIX}__header`}>
        <div className={`${PREFIX}__header-left`}>
          <BasicImage
            src={SKILL_DETAIL_SECTION_ICONS.skillMd}
            alt=""
            className={`${PREFIX}__file-icon`}
          />
          <span className={`${PREFIX}__filename`}>SKILL.md</span>
        </div>
        {sizeText && (
          <span className={`${PREFIX}__size`}>{sizeText}</span>
        )}
      </div>

      <div
        className={classNames(`${PREFIX}__body`, {
          [`${PREFIX}__body--centered`]: isStatusView && !hasAside,
        })}
      >
        <Abnormal
          isLoading={loading}
          isError={Boolean(error)}
          isNoData={!loading && !error && !content}
          style={{ minHeight: 126, height: 126 }}
          classNames={`${PREFIX}__abnormal`}
          component={
            content ? (
              <div className={`${PREFIX}__body-content`}>
                <div className={`${PREFIX}__body-main`}>
                  <ReactMarkdown
                    className={`${PREFIX}__markdown markdown-body`}
                    skipHtml={false}
                    components={components}
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[gfm, breaks]}
                  >
                    {content}
                  </ReactMarkdown>
                </div>
                {aside && (
                  <div className={`${PREFIX}__body-aside`}>
                    {aside}
                  </div>
                )}
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
};

export default memo(SkillMarkdownViewer);
